import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { API, PHONE_DISPLAY, PHONE_LINK, fmtMoney } from "@/lib/data";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", booking: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", booking: null });
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", booking: data.booking });
          return;
        }
      } catch (e) {
        console.error(e);
      }
      tries += 1;
      if (tries < 12) timer = setTimeout(poll, 2000);
      else setState({ phase: "pending", booking: null });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const b = state.booking;

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#06130D] px-5 grain relative" data-testid="payment-success-page">
      <div className="w-full max-w-lg rounded-3xl border border-amber-500/25 bg-[#0B1D14]/90 p-8 text-center gold-glow-soft sm:p-10">
        {state.phase === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-amber-400" size={40} />
            <h1 className="font-display mt-6 text-2xl font-bold text-amber-50">Confirming your deposit…</h1>
            <p className="mt-3 text-sm text-emerald-100/60">Hold tight while we verify your payment with Stripe.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-amber-400" size={48} />
            <h1 className="font-display mt-6 text-3xl font-black text-amber-50" data-testid="success-headline">You're on the schedule!</h1>
            <p className="mt-3 text-sm leading-relaxed text-emerald-100/70">
              Your 50% deposit is confirmed{b ? ` for the ${b.package_name} package` : ""}.
              We'll call you within 24 hours to confirm your install{b?.install_date
                ? ` on ${new Date(b.install_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`
                : ""}.
            </p>
            {b && (
              <div className="mt-6 space-y-2 rounded-2xl border border-amber-500/20 bg-[#06130D]/70 p-5 text-left text-sm" data-testid="success-booking-summary">
                <div className="flex justify-between text-emerald-100/70"><span>Booking reference</span><span className="font-mono-x text-amber-300">{b.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between text-emerald-100/70"><span>Package</span><span className="text-amber-50">{b.package_name}</span></div>
                <div className="flex justify-between text-emerald-100/70"><span>Install date</span><span className="text-amber-50">{b.install_date ? new Date(b.install_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</span></div>
                <div className="flex justify-between text-emerald-100/70"><span>Storage & maintenance</span><span className="text-amber-50">{b.addon ? "Added" : "Not included"}</span></div>
                <div className="flex justify-between text-emerald-100/70"><span>Deposit paid</span><span className="font-bold text-amber-400">{fmtMoney(b.deposit)}</span></div>
                <div className="flex justify-between text-emerald-100/70"><span>Balance after install</span><span>{fmtMoney(b.total - b.deposit)}</span></div>
              </div>
            )}
            <Link to="/" data-testid="success-home-link" className="mt-8 inline-block rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-stone-950 transition-all hover:from-amber-400 hover:to-amber-300 gold-glow">
              Back to Home
            </Link>
          </>
        )}
        {(state.phase === "pending" || state.phase === "error") && (
          <>
            <Loader2 className="mx-auto text-amber-400" size={40} />
            <h1 className="font-display mt-6 text-2xl font-bold text-amber-50">Almost there…</h1>
            <p className="mt-3 text-sm leading-relaxed text-emerald-100/70">
              Your payment is still being confirmed. If you completed checkout, your booking is safe — call us and we'll confirm it right away.
            </p>
            <a href={PHONE_LINK} className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-6 py-3 text-sm font-bold text-amber-300 transition-all hover:bg-amber-500/10">
              <Phone size={15} /> {PHONE_DISPLAY}
            </a>
          </>
        )}
      </div>
    </main>
  );
}
